import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import patternsRadarService from '../../services/patternsRadarService';
import DoubleScrollTable from './components/DoubleScrollTable';
import {
  formatPercent,
  formatCurrency,
  formatMultiplier,
  formatDecimal,
  toNum,
} from './patternsRadarUtils';
import './PatternsRadar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faChartArea,
  faShieldAlt,
  faUsers,
  faLayerGroup,
  faBuilding,
  faCalendarAlt,
  faCoins,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const PatternsRadarSymbolDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [identity, setIdentity] = useState(null);
  const [stats, setStats] = useState(null);
  const [avgDelivery, setAvgDelivery] = useState(null);
  const [peers, setPeers] = useState([]);
  const [peerBasis, setPeerBasis] = useState('');

  // Table Column Filters for Peers (CRM Theme)
  const [peerFilterSymbol, setPeerFilterSymbol] = useState('');
  const [peerFilterName, setPeerFilterName] = useState('');
  const [peerFilterIndustry, setPeerFilterIndustry] = useState('');
  const [peerFilter1D, setPeerFilter1D] = useState('');

  const hasPeerFilters = Boolean(
    peerFilterSymbol || peerFilterName || peerFilterIndustry || peerFilter1D
  );

  const clearPeerFilters = () => {
    setPeerFilterSymbol('');
    setPeerFilterName('');
    setPeerFilterIndustry('');
    setPeerFilter1D('');
  };

  const filteredPeers = useMemo(() => {
    return peers.filter((p) => {
      if (
        peerFilterSymbol &&
        !p.symbol?.toLowerCase().includes(peerFilterSymbol.toLowerCase().trim())
      ) {
        return false;
      }
      if (
        peerFilterName &&
        !p.name?.toLowerCase().includes(peerFilterName.toLowerCase().trim())
      ) {
        return false;
      }
      if (
        peerFilterIndustry &&
        !(p.industry || p.sector || '')
          .toLowerCase()
          .includes(peerFilterIndustry.toLowerCase().trim())
      ) {
        return false;
      }
      if (peerFilter1D === 'gainers' && (toNum(p.ret_1d) ?? 0) <= 0) {
        return false;
      }
      if (peerFilter1D === 'losers' && (toNum(p.ret_1d) ?? 0) >= 0) {
        return false;
      }
      return true;
    });
  }, [peers, peerFilterSymbol, peerFilterName, peerFilterIndustry, peerFilter1D]);

  // Charting state
  const [timeframe, setTimeframe] = useState('1d');
  const [bars, setBars] = useState([]);
  const [barsLoading, setBarsLoading] = useState(false);

  // Indicator overlay state
  const [selectedOverlay, setSelectedOverlay] = useState('none');
  const [indicatorData, setIndicatorData] = useState(null);
  const [indicatorLoading, setIndicatorLoading] = useState(false);

  const fetchSymbolData = async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const [identData, statsData, peersData] = await Promise.all([
        patternsRadarService.getSymbolIdentity(symbol).catch(() => null),
        patternsRadarService.getSymbolStats(symbol, 20).catch(() => null),
        patternsRadarService.getSymbolPeers(symbol, 10).catch(() => null),
      ]);

      setIdentity(identData);
      setStats(statsData?.stats || null);
      setAvgDelivery(statsData?.avg_delivery || null);
      setPeers(peersData?.peers || []);
      setPeerBasis(peersData?.label || peersData?.basis || '');
    } catch (err) {
      console.error('Error fetching symbol details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBars = async (tf = timeframe) => {
    if (!symbol) return;
    setBarsLoading(true);
    try {
      const res = await patternsRadarService.getSymbolBars(symbol, tf);
      const rawBars = res.bars || [];
      const trimmed = rawBars.slice(-150);
      setBars(trimmed);
    } catch (err) {
      console.error('Error fetching bars:', err);
      setBars([]);
    } finally {
      setBarsLoading(false);
    }
  };

  const fetchOverlay = async (expr) => {
    if (!expr || expr === 'none') {
      setIndicatorData(null);
      return;
    }
    // Sift indicator series in v1 are daily metrics
    if (timeframe !== '1d') {
      setTimeframe('1d');
    }
    setIndicatorLoading(true);
    try {
      const res = await patternsRadarService.getSymbolSeries(symbol, [expr], {
        timeframe: '1d',
        sessions: 150,
      });
      setIndicatorData(res);
    } catch (e) {
      console.error('Error fetching indicator series:', e);
      setIndicatorData(null);
    } finally {
      setIndicatorLoading(false);
    }
  };

  const handleToggleOverlay = (expr) => {
    setSelectedOverlay(expr);
    fetchOverlay(expr);
  };

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    // If user leaves 1D, indicators are unavailable in v1 API
    if (tf !== '1d' && selectedOverlay !== 'none') {
      setSelectedOverlay('none');
      setIndicatorData(null);
    }
  };

  useEffect(() => {
    fetchSymbolData();
    fetchBars();
    setSelectedOverlay('none');
    setIndicatorData(null);
  }, [symbol]);

  useEffect(() => {
    fetchBars();
  }, [timeframe]);

  // Merge price bars with computed indicator series by date
  const chartData = useMemo(() => {
    if (!bars || bars.length === 0) return [];
    if (!indicatorData || !indicatorData.columns || indicatorData.columns.length === 0) {
      return bars.map((b) => ({ ...b, indicatorVal: null }));
    }

    const dates = indicatorData.d || [];
    const col = indicatorData.columns[0];
    const values = col?.values || [];
    const dateMap = new Map();
    dates.forEach((dStr, idx) => {
      dateMap.set(dStr, values[idx]);
    });

    return bars.map((b) => {
      const val = dateMap.get(b.d);
      return {
        ...b,
        indicatorVal:
          val !== undefined && val !== null ? Number(Number(val).toFixed(2)) : null,
      };
    });
  }, [bars, indicatorData]);

  // Latest computed indicator value for badge
  const latestIndicator = useMemo(() => {
    if (!indicatorData || !indicatorData.columns || indicatorData.columns.length === 0) {
      return null;
    }
    const col = indicatorData.columns[0];
    const values = col?.values || [];
    const lastVal = values[values.length - 1];
    return {
      label: col.label || selectedOverlay,
      value: lastVal !== undefined && lastVal !== null ? Number(Number(lastVal).toFixed(2)) : null,
    };
  }, [indicatorData, selectedOverlay]);

  return (
    <div className="patterns-radar-container w-100 mt-5 pt-2">
      {/* Breadcrumb & Navigation */}
      <div className="row pb-2 align-items-center">
        <div className="col-md-6">
          <ul className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <a href="/dashboard">
                <i className="ti ti-home"></i> Home
              </a>
            </li>
            <li className="breadcrumb-item">
              <span className="text-muted">Research</span>
            </li>
            <li className="breadcrumb-item">
              <Link to="/research/patterns-radar">Street Analysis</Link>
            </li>
            <li className="breadcrumb-item active font-weight-bold">
              {symbol?.toUpperCase()}
            </li>
          </ul>
        </div>
        <div className="col-md-6 text-end">
          <button
            className="btn btn-default btn-sm"
            onClick={() => navigate('/research/patterns-radar')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1 text-primary" />
            Back to Street Analysis
          </button>
        </div>
      </div>

      {loading ? (
        <div className="panel panel-default with-rounded-corner with-shadow p-5 text-center my-3">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="fs-2 text-primary mb-3"
          />
          <h6 className="text-muted font-weight-bold">
            Loading institutional intelligence for {symbol?.toUpperCase()}...
          </h6>
        </div>
      ) : (
        <>
          {/* 1. Header Profile Banner Card */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-body p-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="font-weight-bold text-dark mb-0">
                      {identity?.display_symbol || symbol?.toUpperCase()}
                    </h4>
                    <span className="badge bg-primary">
                      {identity?.kind?.toUpperCase() || 'EQUITY'}
                    </span>
                    {identity?.series && (
                      <span className="badge bg-light text-dark border">
                        Series: {identity.series}
                      </span>
                    )}
                    {identity?.liquidity_rank && (
                      <span className="badge bg-secondary">
                        NSE Liquidity Rank #{identity.liquidity_rank}
                      </span>
                    )}
                    {identity?.universe_tier && (
                      <span className="badge bg-info text-white">
                        Tier {identity.universe_tier}
                      </span>
                    )}
                  </div>
                  <h6 className="text-muted mb-0 font-weight-normal">
                    {identity?.name || 'National Stock Exchange (NSE)'} • ISIN:{' '}
                    <strong>{identity?.isin || '-'}</strong>
                  </h6>
                </div>

                {stats && (
                  <div className="text-end">
                    <div className="d-flex align-items-baseline gap-2">
                      <h3 className="font-weight-bold text-dark mb-0">
                        {formatCurrency(stats.close)}
                      </h3>
                      <span
                        className={`fs-6 font-weight-bold ${
                          (toNum(stats.ret_1d) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                        }`}
                      >
                        {formatPercent(stats.ret_1d, { showPlus: true })}
                      </span>
                    </div>
                    <small className="text-muted">
                      Evaluated on: <strong>{stats.d}</strong>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2. Performance Horizons Ribbon */}
          {stats && (
            <div className="panel panel-default with-rounded-corner with-shadow mb-3">
              <div className="panel-body py-2 px-3">
                <div className="row text-center g-2">
                  <div className="col-md-2 col-4 border-end pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      1-Day
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_1d) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_1d, { showPlus: true })}
                    </span>
                  </div>
                  <div className="col-md-2 col-4 border-end pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      1-Week
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_1w) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_1w, { showPlus: true })}
                    </span>
                  </div>
                  <div className="col-md-2 col-4 border-end pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      1-Month
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_1m) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_1m, { showPlus: true })}
                    </span>
                  </div>
                  <div className="col-md-2 col-4 border-end pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      3-Month
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_3m) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_3m, { showPlus: true })}
                    </span>
                  </div>
                  <div className="col-md-2 col-4 border-end pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      6-Month
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_6m) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_6m, { showPlus: true })}
                    </span>
                  </div>
                  <div className="col-md-2 col-4 pr-stat-cell">
                    <small className="text-muted d-block font-weight-bold text-uppercase">
                      1-Year
                    </small>
                    <span
                      className={`fs-6 font-weight-bold ${
                        (toNum(stats.ret_1y) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                      }`}
                    >
                      {formatPercent(stats.ret_1y, { showPlus: true })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 3. 52-Week Range Interactive Slider */}
          {stats && (() => {
            const low52 = toNum(stats.low_52w) ?? 0;
            const high52 = toNum(stats.high_52w) ?? 0;
            const closePrice = toNum(stats.close) ?? 0;
            const span = high52 - low52;
            const rawPct = span > 0 ? ((closePrice - low52) / span) * 100 : 50;
            const rangePct = Math.min(100, Math.max(0, rawPct));
            // Clamp pin label position so it doesn't overflow container edge
            const pinPct = Math.min(92, Math.max(8, rangePct));

            return (
              <div className="panel panel-default with-rounded-corner with-shadow mb-3">
                <div className="panel-body p-3">
                  <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                    <span className="font-weight-bold text-dark small text-uppercase">
                      52-Week Price Range
                    </span>
                    <span className="badge bg-light text-dark border">
                      Position: <strong>{rangePct.toFixed(1)}%</strong> of 52W Range
                    </span>
                  </div>

                  {/* Range Slider Track & Floating Pin */}
                  <div className="pr-range-slider-wrapper">
                    <div className="pr-range-pin" style={{ left: `${pinPct}%` }}>
                      <span className="pr-range-pin-label">
                        Current: <strong>{formatCurrency(stats.close)}</strong>
                      </span>
                      <div className="pr-range-pin-arrow" />
                    </div>

                    <div className="pr-range-track">
                      <div className="pr-range-fill" style={{ width: `${rangePct}%` }} />
                      <div className="pr-range-dot" style={{ left: `${rangePct}%` }} />
                    </div>
                  </div>

                  {/* Range Extremes */}
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div>
                      <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '11px' }}>
                        52-Week Low
                      </small>
                      <strong className="fs-6 text-dark">{formatCurrency(stats.low_52w)}</strong>
                      <span className="ms-2 badge bg-success-light text-success font-weight-bold">
                        {formatPercent(stats.pct_from_52w_low, { showPlus: true })} from Low
                      </span>
                    </div>

                    <div className="text-end">
                      <small className="text-muted d-block text-uppercase font-weight-bold" style={{ fontSize: '11px' }}>
                        52-Week High
                      </small>
                      <strong className="fs-6 text-dark">{formatCurrency(stats.high_52w)}</strong>
                      <span className="ms-2 badge bg-danger-light text-danger font-weight-bold">
                        {formatPercent(stats.pct_from_52w_high, { showPlus: false })} from High
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 4. Full-Width Interactive Price Chart */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-heading border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="panel-title font-weight-bold">
                <FontAwesomeIcon icon={faChartArea} className="text-primary me-2" />
                Split-Adjusted OHLCV History ({timeframe.toUpperCase()})
              </span>

              <div className="d-flex align-items-center gap-3">
                {/* Indicator Sift Series Overlay Selector */}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-muted font-weight-bold">
                    <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                    Sift Indicator:
                  </small>
                  <select
                    className="form-control form-control-sm"
                    style={{ width: '190px' }}
                    value={selectedOverlay}
                    onChange={(e) => handleToggleOverlay(e.target.value)}
                  >
                    <option value="none">None (Price Only)</option>
                    <option value="ema(21)">EMA 21</option>
                    <option value="ema(50)">EMA 50</option>
                    <option value="bb(20,2).upper">Bollinger Band Upper</option>
                    <option value="bb(20,2).lower">Bollinger Band Lower</option>
                    <option value="(high + low) / 2">Median Price (H+L)/2</option>
                  </select>

                  {indicatorLoading && (
                    <FontAwesomeIcon icon={faSpinner} spin className="text-warning" />
                  )}

                  {latestIndicator && latestIndicator.value !== null && (
                    <span className="badge bg-warning text-dark border">
                      {latestIndicator.label}: ₹{formatCurrency(latestIndicator.value)}
                    </span>
                  )}
                </div>

                {/* Timeframe Switcher Buttons */}
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn ${
                      timeframe === '1d' ? 'btn-primary' : 'btn-default'
                    }`}
                    onClick={() => handleTimeframeChange('1d')}
                  >
                    Daily (1D)
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      timeframe === '1w' ? 'btn-primary' : 'btn-default'
                    }`}
                    onClick={() => handleTimeframeChange('1w')}
                  >
                    Weekly (1W)
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      timeframe === '1mo' ? 'btn-primary' : 'btn-default'
                    }`}
                    onClick={() => handleTimeframeChange('1mo')}
                  >
                    Monthly (1MO)
                  </button>
                </div>
              </div>
            </div>

            <div className="panel-body p-3">
              <div
                style={{
                  width: '100%',
                  height: 380,
                  backgroundColor: '#ffffff',
                }}
              >
                {barsLoading ? (
                  <div className="text-center py-5 text-muted">
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" />
                    Loading price bars for {symbol}...
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer>
                    <ComposedChart data={chartData}>
                      <defs>
                        <linearGradient
                          id="colorClose"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#127bbc" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#127bbc" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="d"
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        domain={['auto', 'auto']}
                        orientation="right"
                        tickFormatter={(v) => `₹${v}`}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          typeof value === 'number'
                            ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                            : value,
                          name,
                        ]}
                      />
                      <Legend verticalAlign="top" height={36} />
                      <Area
                        type="monotone"
                        dataKey="close"
                        name="Close Price (₹)"
                        stroke="#127bbc"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorClose)"
                      />
                      {selectedOverlay !== 'none' && (
                        <Line
                          type="monotone"
                          dataKey="indicatorVal"
                          name={latestIndicator?.label || selectedOverlay}
                          stroke="#ff9500"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5 text-muted">
                    No bar data available for this timeframe.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 5. Quantitative Technical Health Cards */}
          {stats && (
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading border-0">
                    <span className="panel-title font-weight-bold">
                      Institutional Delivery & Volume
                    </span>
                  </div>
                  <div className="panel-body pt-1">
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">Today Delivery %:</span>
                      <strong className="text-primary fs-6">
                        {formatPercent(stats.delivery_pct, { showPlus: false })}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">20-Session Avg Delivery:</span>
                      <strong>{formatPercent(avgDelivery, { showPlus: false })}</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">Relative Volume:</span>
                      <strong className="text-dark">{formatMultiplier(stats.rel_volume)} normal</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 small">
                      <span className="text-muted">Total Volume:</span>
                      <strong>{toNum(stats.volume) !== null ? Math.round(Number(stats.volume)).toLocaleString('en-IN') : '-'} shares</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading border-0">
                    <span className="panel-title font-weight-bold">
                      Momentum & Volatility Indicators
                    </span>
                  </div>
                  <div className="panel-body pt-1">
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">RSI (14-Day):</span>
                      <strong
                        className={`fs-6 ${
                          toNum(stats.rsi_14) >= 70
                            ? 'text-danger'
                            : toNum(stats.rsi_14) <= 30
                            ? 'text-success'
                            : 'text-dark'
                        }`}
                      >
                        {formatDecimal(stats.rsi_14, { decimals: 1 })}{' '}
                        <small className="font-weight-normal text-muted">
                          {toNum(stats.rsi_14) >= 70
                            ? '(Overbought)'
                            : toNum(stats.rsi_14) <= 30
                            ? '(Oversold)'
                            : '(Neutral)'}
                        </small>
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">ATR 14 (Volatility):</span>
                      <strong>{formatCurrency(stats.atr_14)}</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">Daily Traded Turnover:</span>
                      <strong>
                        ₹{toNum(stats.turnover) !== null ? (stats.turnover / 10000000).toFixed(2) : '0.00'} Cr
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 small">
                      <span className="text-muted">Evaluation Session:</span>
                      <strong>{stats.d}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="panel panel-default with-rounded-corner with-shadow h-100">
                  <div className="panel-heading border-0">
                    <span className="panel-title font-weight-bold">
                      Moving Averages Alignment (SMAs)
                    </span>
                  </div>
                  <div className="panel-body pt-1">
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">20-Day SMA:</span>
                      <strong>{formatCurrency(stats.sma_20)}</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">50-Day SMA:</span>
                      <strong>{formatCurrency(stats.sma_50)}</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 border-bottom small">
                      <span className="text-muted">200-Day SMA:</span>
                      <strong>{formatCurrency(stats.sma_200)}</strong>
                    </div>
                    <div className="d-flex justify-content-between py-2 small">
                      <span className="text-muted">Trend Alignment:</span>
                      <strong
                        className={
                          stats.close > stats.sma_50
                            ? 'text-success'
                            : 'text-danger'
                        }
                      >
                        {stats.close > stats.sma_50
                          ? '✓ Bullish (Price > SMA 50)'
                          : '✗ Bearish (Price < SMA 50)'}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 6. Industry Peer Competitors Table */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-4">
            <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
              <span className="panel-title font-weight-bold">
                <FontAwesomeIcon icon={faUsers} className="text-primary me-2" />
                Industry Peer Comparison Matrix ({peerBasis || 'Sector Peers'})
              </span>
              <span className="small text-muted">
                Showing top competitors by traded liquidity
              </span>
            </div>
            <div className="panel-body p-0">
              <DoubleScrollTable dependencies={[filteredPeers]}>
                <table className="table table-hover table-condensed bg-white m-b-0 sticky-table pr-table">
                  <thead>
                    <tr className="bg-light">
                      <th style={{ minWidth: '120px' }}>Symbol</th>
                      <th style={{ minWidth: '180px' }}>Company Name</th>
                      <th style={{ minWidth: '160px' }}>Sector / Industry</th>
                      <th style={{ minWidth: '110px' }} className="text-end">Last Close</th>
                      <th style={{ minWidth: '110px' }} className="text-end">1D Return</th>
                      <th style={{ minWidth: '130px' }} className="text-end">20D Avg Turnover</th>
                      <th style={{ minWidth: '110px' }} className="text-center">NSE Liq Rank</th>
                      <th style={{ minWidth: '90px' }} className="text-center">Action</th>
                    </tr>
                    <tr>
                      <th>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={peerFilterSymbol}
                          onChange={(e) => setPeerFilterSymbol(e.target.value)}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={peerFilterName}
                          onChange={(e) => setPeerFilterName(e.target.value)}
                        />
                      </th>
                      <th>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={peerFilterIndustry}
                          onChange={(e) => setPeerFilterIndustry(e.target.value)}
                        />
                      </th>
                      <th></th>
                      <th>
                        <select
                          value={peerFilter1D}
                          onChange={(e) => setPeerFilter1D(e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="gainers">Gainers</option>
                          <option value="losers">Losers</option>
                        </select>
                      </th>
                      <th></th>
                      <th></th>
                      <th className="text-center">
                        {hasPeerFilters ? (
                          <button
                            type="button"
                            className="btn btn-default btn-xs btn-clear-filter"
                            onClick={clearPeerFilters}
                            title="Clear filters"
                          >
                            Clear
                          </button>
                        ) : null}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPeers.length > 0 ? (
                      filteredPeers.map((p, idx) => (
                        <tr key={idx}>
                          <td>
                            <Link
                              to={`/research/patterns-radar/symbol/${p.symbol}`}
                              className="font-weight-bold text-primary"
                            >
                              {p.symbol}
                            </Link>
                          </td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: '240px' }}
                            title={p.name}
                          >
                            {p.name || '-'}
                          </td>
                          <td>{p.industry || p.sector || '-'}</td>
                          <td className="text-end font-weight-bold">
                            {formatCurrency(p.last_close)}
                          </td>
                          <td
                            className={`text-end ${
                              (toNum(p.ret_1d) ?? 0) >= 0 ? 'pr-positive' : 'pr-negative'
                            }`}
                          >
                            {formatPercent(p.ret_1d, { showPlus: true })}
                          </td>
                          <td className="text-end">
                            ₹
                            {p.avg_turnover_20d
                              ? (p.avg_turnover_20d / 10000000).toFixed(2)
                              : '0.00'}{' '}
                            Cr
                          </td>
                          <td className="text-center font-weight-bold">
                            {p.liquidity_rank ? `#${p.liquidity_rank}` : '-'}
                          </td>
                          <td className="text-center">
                            <Link
                              to={`/research/patterns-radar/symbol/${p.symbol}`}
                              className="btn btn-default btn-xs text-primary"
                              title="Open this peer"
                            >
                              View Detail
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          {hasPeerFilters
                            ? 'No peer competitors match the active table filters. (Click "Clear" to reset filters)'
                            : 'No peer competitors found in this industry.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </DoubleScrollTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PatternsRadarSymbolDetail;
